import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Button, Spinner, Container } from 'react-bootstrap';
import {
  ShieldCheck,
  XCircle,
  ExclamationTriangle,
  QuestionCircle,
  InfoCircle,
  CalendarEvent,
  BoxArrowUpRight,
  ChevronDown
} from 'react-bootstrap-icons';
import { useTranslation } from 'react-i18next';
import './FactCheckerPage.css';
import openCapacitorSite from '../../utils/browserUtils';
import useCollections, { CollectionModal } from '../../hooks/useCollections';

// ─── Daily token limit helpers ────────────────────────────────────────────────
const STORAGE_KEY = 'gg_factcheck_tokens';
const DAILY_LIMIT = 10;

function getTodayKey() {
  return new Date().toISOString().slice(0, 10); // "YYYY-MM-DD"
}

function getTokensUsed() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return 0;
    const data = JSON.parse(raw);
    if (data.date !== getTodayKey()) return 0;
    return data.used || 0;
  } catch {
    return 0;
  }
}

function consumeToken() {
  const used = getTokensUsed();
  localStorage.setItem(
    STORAGE_KEY,
    JSON.stringify({ date: getTodayKey(), used: used + 1 })
  );
}

function hasTokensLeft() {
  return getTokensUsed() < DAILY_LIMIT;
}

// ─── Fact-check history helpers ───────────────────────────────────────────────
const HISTORY_KEY = 'gg_factcheck_history';
const HISTORY_LIMIT = 25;

function getHistory() {
  try {
    const raw = localStorage.getItem(HISTORY_KEY);
    if (!raw) return [];
    const data = JSON.parse(raw);
    return Array.isArray(data) ? data : [];
  } catch {
    return [];
  }
}

function addToHistory(entry) {
  try {
    const next = [entry, ...getHistory()].slice(0, HISTORY_LIMIT);
    localStorage.setItem(HISTORY_KEY, JSON.stringify(next));
    return next;
  } catch {
    return getHistory();
  }
}

function clearHistory() {
  try {
    localStorage.removeItem(HISTORY_KEY);
  } catch {
    // ignore
  }
}

// ─── Verdict config ───────────────────────────────────────────────────────────
// Labels here are the English fallbacks; actual displayed text is translated
// at render via t(`common:verdict_${verdictKey}`, label) in VerdictBadge.
const VERDICT_CONFIG = {
  CONFIRMED: {
    label: 'Confirmed',
    icon: ShieldCheck,
    colorClass: 'verdict-confirmed',
  },
  REFUTED: {
    label: 'Refuted',
    icon: XCircle,
    colorClass: 'verdict-refuted',
  },
  PARTIALLY_TRUE: {
    label: 'Partially True',
    icon: ExclamationTriangle,
    colorClass: 'verdict-partial',
  },
  MISLEADING: {
    label: 'Misleading',
    icon: ExclamationTriangle,
    colorClass: 'verdict-misleading',
  },
  OUTDATED: {
    label: 'Outdated',
    icon: CalendarEvent,
    colorClass: 'verdict-outdated',
  },
  UNVERIFIABLE: {
    label: 'Unverifiable',
    icon: QuestionCircle,
    colorClass: 'verdict-unverifiable',
  },
  NOT_A_GOVERNMENT_MATTER: {
    label: 'Not a Gov Matter',
    icon: InfoCircle,
    colorClass: 'verdict-not-gov',
  },
};

// ─── Sub-components ───────────────────────────────────────────────────────────

function VerdictBadge({ verdict }) {
  const { t } = useTranslation('common');
  const cfg = VERDICT_CONFIG[verdict] || VERDICT_CONFIG.UNVERIFIABLE;
  const Icon = cfg.icon;
  const verdictKey = (verdict || 'UNVERIFIABLE').toLowerCase();
  return (
    <span className={`verdict-badge ${cfg.colorClass}`}>
      <Icon size={13} className="me-1" />
      {t(`common:verdict_${verdictKey}`, cfg.label)}
    </span>
  );
}

function ConfidenceBar({ confidence }) {
  const { t } = useTranslation('common');
  const pct = Math.round((confidence || 0) * 100);
  let barClass = 'conf-bar-fill';
  if (pct >= 80) barClass += ' conf-high';
  else if (pct >= 50) barClass += ' conf-mid';
  else barClass += ' conf-low';

  return (
    <div className="conf-bar-wrap">
      <div className="conf-bar-track">
        <div className={barClass} style={{ width: `${pct}%` }} />
      </div>
      <span className="conf-bar-label">{t('common:confidencePct', '{{percent}}% confidence', { percent: pct })}</span>
    </div>
  );
}

// ─── XML label maps ───────────────────────────────────────────────────────────
// These are technical fact-check field labels (Original Claim, Verdict, etc.)
// shown in the detailed analysis view. Left as English data — translating
// all 20+ properly requires deeper coordination with the backend XML schema.
const XML_LABELS = {
  original_claim: 'Original Claim',
  government_level: 'Government Level',
  claim_category: 'Claim Category',
  jurisdiction: 'Jurisdiction',
  temporal_scope: 'Temporal Scope',
  authoritative_source: 'Authoritative Source',
  source_url: 'Source URL',
  search_query: 'Search Query',
  domain_restriction: 'Domain Restriction',
  source_located: 'Source Located',
  source_type: 'Source Type',
  source_date: 'Source Date',
  direct_quote: 'Direct Quote',
  paraphrase: 'Paraphrase',
  statistical_data: 'Statistical Data',
  claim_supported: 'Claim Supported',
  support_level: 'Support Level',
  discrepancies: 'Discrepancies',
  verdict: 'Verdict',
  confidence_level: 'Confidence',
  reasoning: 'Reasoning',
  caveats: 'Caveats',
  plain_language_summary: 'Summary',
  recommendation: 'Recommendation',
  fact_check_date: 'Fact-Check Date',
  checker: 'Checker',
  methodology: 'Methodology',
};

const SECTION_ICONS = {
  claim_classification: null,
  source_retrieval: null,
  evidence_extraction: null,
  verification_analysis: null,
  verdict_determination: null,
  final_report: null,
};

// Strip XML tags to plain text
function xmlToText(str) {
  return str.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
}

// Extract first URL from an XML string
function xmlExtractUrl(str) {
  const m = str.match(/<(?:url|source_url|href)>(https?:\/\/[^<\s]+)<\/(?:url|source_url|href)>/);
  return m ? m[1].trim() : (isUrl(str.trim()) ? str.trim() : null);
}

// Recursively parse XML fields — handles nested blocks like <official_source>
function parseXmlFields(xmlStr, depth) {
  const fields = [];
  const tagRegex = /<([a-z_][a-z0-9_]*)>([\s\S]*?)<\/\1>/g;
  let m;
  while ((m = tagRegex.exec(xmlStr)) !== null) {
    const key = m[1];
    const raw = m[2].trim();
    if (!raw) continue;
    // If nested XML and not too deep, expand sub-fields
    if (depth < 2 && /<[a-z_][a-z0-9_]*>/.test(raw)) {
      const sub = parseXmlFields(raw, depth + 1);
      if (sub.length) {
        fields.push(...sub.map(sf => ({
          ...sf,
          label: XML_LABELS[key]
            ? `${XML_LABELS[key]} — ${sf.label}`
            : `${key.replace(/_/g, ' ')} — ${sf.label}`,
        })));
        continue;
      }
    }
    fields.push({ key, label: XML_LABELS[key] || key.replace(/_/g, ' '), value: raw });
  }
  return fields;
}

function parseAnalysisSections(raw) {
  if (!raw) return [];
  const text = raw.replace(/^##\s*Verdict:.*\n?/i, '').trim();
  const sections = [];
  const blockRegex = /\*\*([^*]+)\*\*[\s\S]*?```xml\s*([\s\S]*?)```/g;
  let match;
  let lastIndex = 0;
  while ((match = blockRegex.exec(text)) !== null) {
    const heading = match[1].trim();
    const xmlStr = match[2].trim();
    lastIndex = blockRegex.lastIndex;
    // Strip root wrapper tag
    const rootMatch = xmlStr.match(/^<([a-z_][a-z0-9_]*)>/);
    const rootTag = rootMatch ? rootMatch[1] : null;
    const innerXml = rootTag
      ? xmlStr.replace(new RegExp(`^<${rootTag}>`), '').replace(new RegExp(`<\/${rootTag}>\\s*$`), '')
      : xmlStr;
    const fields = parseXmlFields(innerXml, 0);
    sections.push({ heading, rootTag, fields, icon: null });
  }
  const remainder = text.slice(lastIndex).trim()
    .replace(/```xml[\s\S]*?```/g, '')
    .replace(/\*\*[^*]+\*\*/g, '')
    .trim();
  // 'Additional Notes' heading is translated at render site, not here.
  if (remainder) sections.push({ heading: 'Additional Notes', fields: [], plainText: remainder, icon: null });
  return sections;
}

function isUrl(val) { return /^https?:\/\//.test(val.trim()); }

function FieldValue({ value }) {
  // Value might still contain XML tags if nesting was deep — clean it first
  const embeddedUrl = xmlExtractUrl(value);
  const cleanVal = xmlToText(value);

  if (embeddedUrl || isUrl(cleanVal)) {
    const url = embeddedUrl || cleanVal.trim();
    return (
      <button type="button" className="fc-field-link" onClick={() => openCapacitorSite(url, '_blank')}>
        <BoxArrowUpRight size={11} className="me-1" />{url}
      </button>
    );
  }
  if (cleanVal === 'YES' || cleanVal === 'NO') {
    return <span className={`fc-pill ${cleanVal === 'YES' ? 'fc-pill-yes' : 'fc-pill-no'}`}>{cleanVal}</span>;
  }
  if (/^[A-Z_]{3,}$/.test(cleanVal)) {
    return <span className="fc-pill fc-pill-status">{cleanVal.replace(/_/g, ' ')}</span>;
  }
  return <span className="fc-field-val">{cleanVal}</span>;
}

function FullAnalysis({ raw }) {
  const { t } = useTranslation('common');
  const sections = parseAnalysisSections(raw);
  if (!sections.length) return <div className="fc-result-full">{raw}</div>;
  return (
    <div className="fc-analysis-wrap">
      {sections.map((sec, i) => (
        <div key={i} className="fc-analysis-section">
          <div className="fc-analysis-heading">
            {sec.heading === 'Additional Notes'
              ? t('common:additionalNotes', 'Additional Notes')
              : sec.heading}
          </div>
          {sec.plainText ? (
            <p className="fc-analysis-plain">{sec.plainText}</p>
          ) : (
            <div className="fc-analysis-fields">
              {sec.fields.map((f, j) => (
                <div key={j} className="fc-analysis-field">
                  <div className="fc-field-label">{f.label}</div>
                  <FieldValue value={f.value} />
                </div>
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

// Extract all source URLs + names from both result.sources and the XML in result.result
function extractSources(result) {
  const sources = [];
  const seen = new Set();

  const addSource = (url, name) => {
    const cleanUrl = url.trim();
    if (!cleanUrl || seen.has(cleanUrl)) return;
    seen.add(cleanUrl);
    sources.push({ url: cleanUrl, name: (name || cleanUrl).trim() });
  };

  if (result.result) {
    // Match ALL url-like tags: <source_url>, <url>, <href>
    const urlRe = /<(?:source_url|url|href)>(https?:\/\/[^<\s]+)<\/(?:source_url|url|href)>/g;
    // Collect sibling <name> / <authoritative_source> values to pair with URLs
    const nameRe = /<(?:name|authoritative_source|source_name)>([^<]+)<\/(?:name|authoritative_source|source_name)>/g;
    const allNames = [...result.result.matchAll(nameRe)].map(m => m[1].trim());

    let i = 0;
    for (const m of result.result.matchAll(urlRe)) {
      addSource(m[1], allNames[i]);
      i++;
    }
  }

  // Fall back to result.sources if XML had nothing
  if (sources.length === 0 && result.sources && result.sources.length > 0) {
    for (const src of result.sources) {
      if (typeof src === 'string' && src.startsWith('http')) {
        addSource(src, src);
      } else if (src && typeof src === 'object') {
        const url = Object.values(src).find(v => typeof v === 'string' && v.startsWith('http')) || '';
        const name = src.name || src.title || src.agency || src.source || url;
        if (url) addSource(url, name);
      }
    }
  }

  return sources;
}

function SourceLinks({ result }) {
  const { t } = useTranslation('common');
  const sources = extractSources(result);
  if (!sources.length) return null;
  return (
    <div className="fc-sources">
      <div className="fc-sources-label">{t('common:sourcesHeading', 'Sources')}</div>
      {sources.map((src, i) => (
        <button
          key={i}
          type="button"
          className="fc-source-link"
          onClick={() => openCapacitorSite(src.url, '_blank')}
        >
          <BoxArrowUpRight size={11} className="me-1" />
          {src.name}
        </button>
      ))}
    </div>
  );
}

function FactCheckResultCard({ result }) {
  const { t } = useTranslation('common');
  const [expanded, setExpanded] = useState(false);

  const {
    isModalOpen, currentItem, saveSuccess,
    note, setNote, closeModal, handleSave,
    isStorageAvailable, CollectionButton,
  } = useCollections();

  const sources = extractSources(result);
  const pct = Math.round((result.confidence || 0) * 100);
  const formattedDate = new Date(result.timestamp).toLocaleDateString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric'
  });

  // Stored collection fields use English values (data convention).
  // keyFigure shown in collection list will be in whatever language was active at save time.
  const collectionItem = {
    title: result.claim,
    url: sources[0]?.url || '',
    docLink: sources[1]?.url || '',
    date: result.timestamp,
    feedName: `Fact Check — ${result.verdict}`,
    branch: 'Gov Glance Fact Checker',
    topic: 'Fact Check',
    type: 'fact',
    keyFigure: `${VERDICT_CONFIG[result.verdict]?.label || result.verdict} — ${pct}% confidence`,
    factText: result.summary || '',
    fullResult: result.result || '',
    allSources: JSON.stringify(sources),
    source: JSON.stringify(sources[0] || {}),
    context: '',
    tags: '[]',
  };

  return (
    <>
      <div className="fact-card fc-result-card-new">

        {/* Top row: verdict badge + date */}
        <div className="fact-card-top">
          <VerdictBadge verdict={result.verdict} />
          <span className="fact-date">{formattedDate}</span>
        </div>

        {/* Claim as title */}
        <div className="fact-title">"{result.claim}"</div>

        {/* Confidence as key figure */}
        <div className="fact-key-figure">{t('common:confidencePct', '{{percent}}% confidence', { percent: pct })}</div>

        {/* Summary as body */}
        <div className="fact-body" style={{ WebkitLineClamp: 'unset', overflow: 'visible' }}>
          {result.summary}
        </div>

        {/* Full analysis toggle */}
        {result.result && (
          <>
            <button
              className="fc-expand-btn"
              onClick={() => setExpanded(v => !v)}
            >
              {expanded
                ? t('common:hideFullAnalysis', 'Hide full analysis')
                : t('common:fullAnalysis', 'Full analysis')}
              <ChevronDown
                size={13}
                className={`fc-chevron ${expanded ? 'fc-chevron-up' : ''}`}
              />
            </button>
            {expanded && <FullAnalysis raw={result.result} />}
          </>
        )}

        {/* Sources */}
        <SourceLinks result={result} />

        {/* Footer */}
        <div className="fact-footer">
          <span className="fact-date" />
          <CollectionButton item={collectionItem} />
        </div>
      </div>

      <CollectionModal
        isModalOpen={isModalOpen}
        currentItem={currentItem}
        saveSuccess={saveSuccess}
        note={note}
        setNote={setNote}
        handleSave={handleSave}
        closeModal={closeModal}
        isStorageAvailable={isStorageAvailable}
      />
    </>
  );
}

function FactCard({ fact }) {
  const {
    isModalOpen, currentItem, saveSuccess,
    note, setNote, closeModal, handleSave,
    isStorageAvailable, CollectionButton,
  } = useCollections();

  const collectionItem = {
    title: fact.key_figure ? `${fact.title} — ${fact.key_figure}` : fact.title,
    url: fact.source?.url || '',
    docLink: '',
    date: fact.date_of_data,
    feedName: fact.category || 'Gov Glance Facts',
    branch: fact.source?.agency || '',
    topic: fact.category || '',
    type: 'fact',
    keyFigure: fact.key_figure || '',
    factText: fact.fact || '',
    context: fact.context || '',
    tags: JSON.stringify(fact.tags || []),
    source: JSON.stringify(fact.source || {}),
  };

  return (
    <>
      <div className="fact-card">
        <div className="fact-card-top" style={{ justifyContent: 'flex-end' }}>
          <span className="fact-date">
            {fact.date_of_data
              ? new Date(fact.date_of_data).toLocaleDateString('en-US', {
                  month: 'short', day: 'numeric', year: 'numeric'
                })
              : ''}
          </span>
        </div>

        <div className="fact-title">{fact.title}</div>

        {fact.key_figure && (
          <div className="fact-key-figure">{fact.key_figure}</div>
        )}

        <div className="fact-body">{fact.fact}</div>

        {fact.context && (
          <div className="fact-context">{fact.context}</div>
        )}

        <div className="fact-footer">
          <button
            type="button"
            className="fact-source-link"
            onClick={() => openCapacitorSite(fact.source?.url, '_blank')}
          >
            <BoxArrowUpRight size={11} className="me-1" />
            {fact.source?.agency || fact.source?.name}
          </button>
          <CollectionButton item={collectionItem} />
        </div>
      </div>

      <CollectionModal
        isModalOpen={isModalOpen}
        currentItem={currentItem}
        saveSuccess={saveSuccess}
        note={note}
        setNote={setNote}
        handleSave={handleSave}
        closeModal={closeModal}
        isStorageAvailable={isStorageAvailable}
      />
    </>
  );
}



// ─── Main page ────────────────────────────────────────────────────────────────

const FactCheckerPage = () => {
  const { t } = useTranslation('common');
  const [claim, setClaim] = useState('');
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [tokensUsed, setTokensUsed] = useState(getTokensUsed);
  const [history, setHistory] = useState(getHistory);

  // Facts browser (shuffled feed only)
  const [facts, setFacts] = useState([]);
  const [factsLoading, setFactsLoading] = useState(false);
  const [factsTotal, setFactsTotal] = useState(0);
  const [factsOffset, setFactsOffset] = useState(0);

  const textareaRef = useRef(null);
  const resultRef = useRef(null);

  // Auto-resize textarea
  const handleClaimChange = (e) => {
    setClaim(e.target.value);
    const ta = textareaRef.current;
    if (ta) {
      ta.style.height = 'auto';
      ta.style.height = `${ta.scrollHeight}px`;
    }
  };

  // Fetch facts (always shuffled)
  const fetchFacts = useCallback(async (offset = 0, append = false) => {
    setFactsLoading(true);
    try {
      const params = new URLSearchParams({
        limit: 12,
        offset,
        sort: 'random',
      });

      const res = await fetch(
        `https://factcheck.govglance.org/api/v1/facts?${params}`
      );
      if (!res.ok) throw new Error('Failed to fetch facts');
      const data = await res.json();
      setFactsTotal(data.total || 0);
      setFacts(prev => append ? [...prev, ...(data.facts || [])] : (data.facts || []));
      setFactsOffset(offset);
    } catch (err) {
      console.error(err);
    } finally {
      setFactsLoading(false);
    }
  }, []);

  // Initial facts load
  useEffect(() => {
    fetchFacts(0, false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const limitRef = useRef(null);

  const handleVerify = async () => {
    if (!claim.trim()) return;
    // Refresh token count from storage (handles cross-tab / returning users)
    const currentUsed = getTokensUsed();
    setTokensUsed(currentUsed);
    if (currentUsed >= DAILY_LIMIT) {
      setTimeout(() => limitRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 50);
      return;
    }

    setLoading(true);
    setError(null);
    setResult(null);

    try {
      const res = await fetch('https://factcheck.govglance.org/api/v1/fact-check', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ claim: claim.trim() }),
      });

      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData.error || `Server error (${res.status})`);
      }

      const data = await res.json();
      if (!data.success) throw new Error(data.error || 'Verification failed');

      consumeToken();
      setTokensUsed(getTokensUsed());
      setResult(data);
      setHistory(addToHistory(data));

      setTimeout(() => {
        resultRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);
    } catch (err) {
      setError(t('common:errorVerifyingClaim', 'Error verifying that claim. Please try again later.'));
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) handleVerify();
  };

  const remaining = DAILY_LIMIT - tokensUsed;

  // When a fresh result is shown it already sits at history[0]; skip it here so
  // it isn't rendered twice. With no active result, show the full history.
  const historyToShow = result ? history.slice(1) : history;

  return (
    <>
    <div id="homebg" className="fc-page">
      {/* ── Hero / input section ── */}
      <div className="fc-hero">
        <div className="fc-hero-inner">



          <div className="fc-input-card">
            <textarea
              ref={textareaRef}
              className="fc-textarea" style={{ fontSize: "16px" }}
              placeholder={t('common:enterClaimPlaceholder', 'Enter a claim to verify…  (e.g. "The federal minimum wage is $15 per hour")')}
              value={claim}
              onChange={handleClaimChange}
              onKeyDown={handleKeyDown}
              rows={3}
              disabled={loading}
            />
            <div className="fc-input-footer">
              <span className="fc-hint">
                {t('common:factCheckHint', 'We verify claims using only official .gov sources')}
              </span>
              <Button
                className="fc-verify-btn"
                onClick={handleVerify}
                disabled={loading || !claim.trim()}
              >
                {loading ? (
                  <><Spinner animation="border" size="sm" className="me-2" />{t('common:verifying', 'Verifying…')}</>
                ) : (
                  <><ShieldCheck size={16} className="me-2" />{t('common:verify', 'Verify')}</>
                )}
              </Button>
            </div>
          </div>

          {error && <div className="fc-error-msg">{error}</div>}

          {tokensUsed >= DAILY_LIMIT && (
            <div className="ai-limit-message" ref={limitRef}>
              <h5>{t('common:factCheckLimitTitle', 'Fact Check Limit Reached')}</h5>
              <p>{t('common:factCheckLimitMessage', "You've used your 10 free Fact Checks for today.")}</p>
              <a
                href="https://factcheck.govglance.org/"
                target="_blank"
                rel="noopener noreferrer"
              >
                {t('common:getMoreFactChecks', 'Get More Fact Checks →')}
              </a>
            </div>
          )}
        </div>
      </div>

      {/* ── Result card ── */}
      {result && (
        <div className="fc-browser-section fc-result-wrap" ref={resultRef}>
          <Container className="fc-container">
            <h2 className="fc-browser-title">{t('common:yourResult', 'Your Result')}</h2>
            <div className="fc-facts-grid" style={{ gridTemplateColumns: '1fr' }}>
              <FactCheckResultCard result={result} />
            </div>

            <p className="fc-disclaimer fc-result-disclaimer">
              <strong>{t('common:disclaimerLabel', 'Disclaimer:')}</strong>{' '}
              {t('common:factCheckDisclaimer', 'This fact-check was generated by AI and may contain errors. Always verify information by consulting the primary .gov sources linked above. AI-generated content should be used as a starting point for research, not as definitive truth.')}
            </p>

            <div className="fc-load-more-wrap" style={{ paddingTop: 20 }}>
              <Button
                className="fc-load-more-btn"
                onClick={() => {
                  setResult(null);
                  setClaim('');
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                  setTimeout(() => textareaRef.current?.focus(), 400);
                }}
              >
                {t('common:checkAnotherFact', 'Check Another Fact')}
              </Button>
            </div>
          </Container>
        </div>
      )}

      {/* ── Recent fact checks (history) ── */}
      {historyToShow.length > 0 && (
        <div className="fc-browser-section fc-history-section">
          <Container className="fc-container">
            <div className="fc-history-header">
              <h2 className="fc-browser-title" style={{ marginBottom: 0 }}>
                {t('common:recentFactChecks', 'Recent Fact Checks')}
              </h2>
              <button
                type="button"
                className="fc-clear-history-btn"
                onClick={() => { clearHistory(); setHistory([]); }}
              >
                {t('common:clearHistory', 'Clear history')}
              </button>
            </div>
            <div className="fc-facts-grid" style={{ gridTemplateColumns: '1fr' }}>
              {historyToShow.map((h, i) => (
                <FactCheckResultCard key={h.timestamp || i} result={h} />
              ))}
            </div>
          </Container>
        </div>
      )}

      {/* ── Facts browser (shuffled feed) ── */}
      <div className="fc-browser-section fc-feed-section">
        <Container className="fc-container">

          {/* Grid */}
          {factsLoading && facts.length === 0 ? (
            <div className="fc-facts-loading">
              <Spinner animation="grow" />
            </div>
          ) : (
            <>
              <div className="fc-facts-grid">
                {facts.map(fact => (
                  <FactCard key={fact.id} fact={fact} />
                ))}
              </div>

              {facts.length < factsTotal && (
                <div className="fc-load-more-wrap">
                  <Button
                    className="fc-load-more-btn"
                    onClick={() => fetchFacts(factsOffset + 12, true)}
                    disabled={factsLoading}
                  >
                    {factsLoading
                      ? <Spinner animation="border" size="sm" />
                      : t('common:loadMoreRemaining', 'Load More ({{count}} remaining)', { count: factsTotal - facts.length })}
                  </Button>
                </div>
              )}

              {facts.length === 0 && !factsLoading && (
                <div className="fc-no-facts">{t('common:noFactsFound', 'No facts found.')}</div>
              )}
            </>
          )}
        </Container>
      </div>
    </div>

    </>
  );
};

export default FactCheckerPage;